import intl from 'react-intl-universal';
import LegendSymbol from '@ferlab/ui/core/components/Charts/LegendSymbol';
import PieChart from '@ferlab/ui/core/components/Charts/Pie';
import Empty from '@ferlab/ui/core/components/Empty';
import { updateActiveQueryField } from '@ferlab/ui/core/components/QueryBuilder/utils/useQueryBuilderState';
import { ArrangerValues } from '@ferlab/ui/core/data/arranger/formatting';
import ResizableGridCard from '@ferlab/ui/core/layout/ResizableGridLayout/ResizableGridCard';
import {
  aggregationToChartData,
  formatAggregationChartData,
} from '@ferlab/ui/core/layout/ResizableGridLayout/utils';
import { Col, Row } from 'antd';
import { INDEXES } from 'graphql/constants';
import useParticipantResolvedSqon from 'graphql/participants/useParticipantResolvedSqon';
import { DEMOGRAPHIC_QUERY } from 'graphql/summary/queries';
import { isEmpty } from 'lodash';
import { ARRANGER_API_PROJECT_URL } from 'provider/ApolloProvider';
import { DATA_EXPLORATION_QB_ID } from 'views/DataExploration/utils/constant';

import { getCommonColors } from 'common/charts';
import useApi from 'hooks/useApi';
import { getResizableGridDictionary } from 'utils/translation';

import { DEMOGRAPHICS_GRAPH_CARD_ID, UID } from '../utils/grid';

import styles from './index.module.css';

const colors = getCommonColors();

const graphSetting = {
  margin: {
    top: 0,
    bottom: 8,
    left: 12,
    right: 12,
  },
};

const graphModalSettings = {
  margin: {
    top: -142,
    bottom: 160,
    left: 12,
    right: 12,
  },
};

const LEGEND_ITEM_HEIGHT = 18;

const addToQuery = (field: string, key: string) =>
  updateActiveQueryField({
    queryBuilderId: DATA_EXPLORATION_QB_ID,
    field,
    value: [key.toLowerCase() === 'no data' ? ArrangerValues.missing : key],
    index: INDEXES.PARTICIPANT,
  });

const DemographicsGraphCard = () => {
  const { sqon } = useParticipantResolvedSqon(DATA_EXPLORATION_QB_ID);
  const { loading, result } = useApi<any>({
    config: {
      url: ARRANGER_API_PROJECT_URL,
      method: 'POST',
      data: {
        query: DEMOGRAPHIC_QUERY,
        variables: { sqon },
      },
    },
  });

  const raceData = aggregationToChartData(
    result?.data?.participant?.aggregations?.race?.buckets,
    result?.data?.participant?.hits?.total,
  );
  const sexData = aggregationToChartData(
    result?.data?.participant?.aggregations?.sex?.buckets,
    result?.data?.participant?.hits?.total,
  );
  const ethnicityData = aggregationToChartData(
    result?.data?.participant?.aggregations?.ethnicity?.buckets,
    result?.data?.participant?.hits?.total,
  );

  return (
    <ResizableGridCard
      gridUID={UID}
      id={DEMOGRAPHICS_GRAPH_CARD_ID}
      dictionary={getResizableGridDictionary()}
      theme="shade"
      loading={loading}
      loadingType="spinner"
      headerTitle={intl.get('screen.dataExploration.tabs.summary.demographic.cardTitle')}
      tsvSettings={{
        contentMap: ['label', 'value', 'frequency'],
        data: [
          formatAggregationChartData(sexData),
          formatAggregationChartData(raceData),
          formatAggregationChartData(ethnicityData),
        ],
        headers: [
          ['Sex', 'Count', 'Frequency'],
          ['Race', 'Count', 'Frequency'],
          ['Ethnicity', 'Count', 'Frequency'],
        ],
      }}
      modalContent={
        <Row gutter={[12, 24]} className={styles.graphRowWrapper}>
          <Col sm={12} md={12} lg={8}>
            <PieChart
              data={sexData}
              colors={colors}
              legends={[
                {
                  anchor: 'bottom',
                  translateX: 0,
                  translateY: (LEGEND_ITEM_HEIGHT * sexData.length - 1) / 2,
                  symbolShape: (props) => <LegendSymbol {...props} data={sexData} />,
                  direction: 'column',
                  itemWidth: 100,
                  itemHeight: LEGEND_ITEM_HEIGHT,
                },
              ]}
              {...graphModalSettings}
            />
          </Col>
          <Col sm={12} md={12} lg={8}>
            <PieChart
              data={ethnicityData}
              colors={colors}
              legends={[
                {
                  anchor: 'bottom',
                  translateX: 0,
                  translateY: (LEGEND_ITEM_HEIGHT * ethnicityData.length - 1) / 2,
                  symbolShape: (props) => <LegendSymbol {...props} data={ethnicityData} />,
                  direction: 'column',
                  itemWidth: 100,
                  itemHeight: LEGEND_ITEM_HEIGHT,
                },
              ]}
              {...graphModalSettings}
            />
          </Col>
          <Col sm={12} md={12} lg={8}>
            <PieChart
              data={raceData}
              colors={colors}
              legends={[
                {
                  anchor: 'bottom',
                  translateX: 0,
                  translateY: (LEGEND_ITEM_HEIGHT * raceData.length - 1) / 2,
                  symbolShape: (props) => <LegendSymbol {...props} data={raceData} />,
                  direction: 'column',
                  itemWidth: 160,
                  itemHeight: LEGEND_ITEM_HEIGHT,
                },
              ]}
              {...graphModalSettings}
            />
          </Col>
        </Row>
      }
      modalSettings={{
        width: 1000,
        height: 700,
      }}
      content={
        <Row gutter={[12, 24]} className={styles.graphRowWrapper}>
          <Col sm={12} md={12} lg={8}>
            {isEmpty(sexData) ? (
              <Empty imageType="grid" noPadding description={intl.get('api.noData')} />
            ) : (
              <PieChart
                title={intl.get('screen.dataExploration.tabs.summary.demographic.sexTitle')}
                data={sexData}
                onClick={(datum) => addToQuery('sex', datum.id as string)}
                colors={colors}
                {...graphSetting}
              />
            )}
          </Col>
          <Col sm={12} md={12} lg={8}>
            {isEmpty(ethnicityData) ? (
              <Empty imageType="grid" noPadding />
            ) : (
              <PieChart
                title={intl.get('screen.dataExploration.tabs.summary.demographic.ethnicityTitle')}
                data={ethnicityData}
                onClick={(datum) => addToQuery('ethnicity', datum.id as string)}
                colors={colors}
                {...graphSetting}
              />
            )}
          </Col>
          <Col sm={12} md={12} lg={8}>
            {isEmpty(raceData) ? (
              <Empty imageType="grid" noPadding />
            ) : (
              <PieChart
                title={intl.get('screen.dataExploration.tabs.summary.demographic.raceTitle')}
                data={raceData}
                colors={colors}
                onClick={(datum) => addToQuery('race', datum.id as string)}
                {...graphSetting}
              />
            )}
          </Col>
        </Row>
      }
    />
  );
};

export default DemographicsGraphCard;
